import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { contractsApi, ContractActionLog, ContractType } from '../../api/contractsApi';
import { repairUploadFileName } from '../../utils/uploadFileName';
import { BulkListPagination } from '../../components/bulk-fee/BulkListPagination';
import { HISTORY_PAGE_SIZE } from '../../api/paginatedHistory';
import { useLang } from '../../contexts/LangContext';

const ACTION_LABELS: Record<ContractActionLog['action'], string> = {
  CREATE: 'Tạo mới',
  UPDATE: 'Cập nhật',
  DELETE: 'Xóa',
  FILE_UPLOAD: 'Tải file lên',
  FILE_DELETE: 'Xóa file',
  GENERATE_INVOICES: 'Tạo hóa đơn',
  IMPORT: 'Nhập dữ liệu',
  EXPORT: 'Xuất dữ liệu',
};

const getActionBadgeClass = (action: ContractActionLog['action']) => {
  switch (action) {
    case 'CREATE':
      return 'bg-emerald-100 text-emerald-700';
    case 'UPDATE':
      return 'bg-cyan-100 text-cyan-700';
    case 'DELETE':
    case 'FILE_DELETE':
      return 'bg-red-100 text-red-700';
    case 'FILE_UPLOAD':
      return 'bg-blue-100 text-blue-700';
    default:
      return 'bg-gray-100 text-gray-700';
  }
};

interface ContractHistoryTabProps {
  buildingId: string;
  contractType: ContractType;
}

export const ContractHistoryTab: React.FC<ContractHistoryTabProps> = ({ buildingId, contractType }) => {
  const { t } = useLang();
  const [logs, setLogs] = useState<ContractActionLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const loadLogs = useCallback(async () => {
    if (!buildingId) return;
    setLoading(true);
    try {
      const result = await contractsApi.getActionLogs(buildingId, {
        contractType,
        page,
        limit: HISTORY_PAGE_SIZE,
      });
      setLogs(result.data);
      setTotal(result.meta.total);
      setTotalPages(result.meta.totalPages);
    } catch {
      toast.error('Không thể tải lịch sử thao tác');
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [buildingId, contractType, page]);

  useEffect(() => {
    setPage(1);
  }, [buildingId, contractType]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  return (
    <div className="bg-white border rounded-lg overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left">Thời gian</th>
            <th className="px-4 py-3 text-left">Thao tác</th>
            <th className="px-4 py-3 text-left">Mã HĐ</th>
            <th className="px-4 py-3 text-left">Chi tiết</th>
            <th className="px-4 py-3 text-left">Người thực hiện</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr><td colSpan={5} className="px-4 py-10 text-center text-gray-500">Đang tải...</td></tr>
          ) : logs.length === 0 ? (
            <tr><td colSpan={5} className="px-4 py-10 text-center text-gray-500">Chưa có lịch sử</td></tr>
          ) : logs.map((log) => (
            <tr key={log.id} className="border-t">
              <td className="px-4 py-3 whitespace-nowrap">
                {new Date(log.createdAt).toLocaleString('vi-VN')}
              </td>
              <td className="px-4 py-3">
                <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getActionBadgeClass(log.action)}`}>
                  {ACTION_LABELS[log.action] || log.action}
                </span>
              </td>
              <td className="px-4 py-3">{log.contractCode || '—'}</td>
              <td className="px-4 py-3">
                {log.summary?.includes(': ')
                  ? `${log.summary.slice(0, log.summary.indexOf(': ') + 2)}${repairUploadFileName(
                      log.summary.slice(log.summary.indexOf(': ') + 2),
                    )}`
                  : log.summary}
              </td>
              <td className="px-4 py-3">{log.creator?.name || log.creator?.email || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="p-4">
        <BulkListPagination
          page={page}
          totalPages={totalPages}
          total={total}
          onPageChange={setPage}
          t={t}
        />
      </div>
    </div>
  );
};

export default ContractHistoryTab;
